import { useEffect, useState } from 'react'
import Chart from 'chart.js'

// Light/dark mode for the admin. The palette lives in index.css; this module
// decides which one is active and tells the parts of the page that cannot
// read CSS variables (Chart.js canvases) when it changes.
//
// Without a stored preference the mode follows the system setting, so the
// page already paints in the right palette before this script runs (the CSS
// uses prefers-color-scheme for that). A preference pins the palette through
// data-bs-theme on <html>, which Bootstrap's own components also read.

export const STORAGE_KEY = 'msp-admin-theme'
export const MODES = ['light', 'dark']

const DARK_QUERY = '(prefers-color-scheme: dark)'
const listeners = new Set()

const canUseDom = () => typeof window !== 'undefined' &&
  typeof document !== 'undefined'

export const getSystemMode = () => {
  if (!canUseDom() || typeof window.matchMedia !== 'function') {
    return 'dark'
  }
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light'
}

// The stored choice, or null when the admin follows the system.
export const getStoredPreference = () => {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY)
    return MODES.includes(value) ? value : null
  } catch (error) {
    return null
  }
}

export const resolveMode = (preference, systemMode) =>
  MODES.includes(preference) ? preference : systemMode

export const getMode = () => resolveMode(getStoredPreference(), getSystemMode())

const readToken = (name) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim()

// Canvas charts cannot resolve var(), so hand Chart.js the resolved colours.
// New charts pick up the defaults; existing ones get their scales patched.
const applyChartTheme = () => {
  if (!Chart.defaults) {
    return
  }
  const text = readToken('--msp-text-secondary')
  const grid = readToken('--msp-border-color')
  const zero = readToken('--msp-text-muted')
  // Bootstrap's body stack, so chart labels match the page.
  const font = readToken('--bs-body-font-family')

  Chart.defaults.global.defaultFontColor = text
  if (font) {
    Chart.defaults.global.defaultFontFamily = font
  }
  Chart.defaults.scale.gridLines.color = grid
  Chart.defaults.scale.gridLines.zeroLineColor = zero

  Object.values(Chart.instances || {}).forEach((chart) => {
    try {
      const options = chart.options || {}
      if (options.legend && options.legend.labels) {
        options.legend.labels.fontColor = text
      }
      const scales = options.scales || {}
      const axes = [].concat(scales.xAxes || [], scales.yAxes || [])
      axes.forEach((axis) => {
        axis.ticks = { ...axis.ticks, fontColor: text }
        axis.gridLines = { ...axis.gridLines, color: grid, zeroLineColor: zero }
      })
      chart.update()
    } catch (error) {
      // A chart mid-destroy is not worth failing the toggle for.
    }
  })
}

const notify = () => {
  const mode = getMode()
  listeners.forEach((listener) => listener(mode))
}

export const applyTheme = () => {
  if (!canUseDom()) {
    return
  }
  const preference = getStoredPreference()
  const root = document.documentElement
  if (preference) {
    root.setAttribute('data-bs-theme', preference)
  } else {
    // Bootstrap's own components only follow data-bs-theme, never the system
    // setting, so mirror the system mode on the attribute.
    root.setAttribute('data-bs-theme', getSystemMode())
  }
  applyChartTheme()
  notify()
}

export const setPreference = (preference) => {
  try {
    if (MODES.includes(preference)) {
      window.localStorage.setItem(STORAGE_KEY, preference)
    } else {
      window.localStorage.removeItem(STORAGE_KEY)
    }
  } catch (error) {
    // Private mode or a full store: the choice lasts for this page only.
  }
  applyTheme()
}

export const toggleMode = () => {
  setPreference(getMode() === 'dark' ? 'light' : 'dark')
}

// Call once at startup: applies the stored or system palette and keeps
// following the system until the admin picks a mode.
export const initTheme = () => {
  if (!canUseDom()) {
    return
  }
  applyTheme()
  if (typeof window.matchMedia === 'function') {
    const query = window.matchMedia(DARK_QUERY)
    const onChange = () => {
      if (!getStoredPreference()) {
        applyTheme()
      }
    }
    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', onChange)
    } else if (typeof query.addListener === 'function') {
      query.addListener(onChange)
    }
  }
}

export const subscribe = (listener) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

// The active mode, re-rendering the caller when it changes.
export const useThemeMode = () => {
  const [mode, setMode] = useState(getMode)
  useEffect(() => subscribe(setMode), [])
  return mode
}
